import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import { CareerRolePopulationRunResponse } from '../../../../models';

const STORAGE_KEY = 'csr-admin-tracked-runs';
const TERMINAL = new Set(['completed', 'failed']);
const POLL_MS = 4000;

export interface TrackedRunContext {
  runId: string;
  label: string;
  roleSlug?: string;
  operationType?: string;
  onComplete?: () => void;
}

export interface TrackedRunState extends TrackedRunContext {
  run: CareerRolePopulationRunResponse | null;
  dismissed: boolean;
}

interface PersistedRun {
  runId: string;
  label: string;
  roleSlug?: string;
  operationType?: string;
}

@Injectable({ providedIn: 'root' })
export class BackgroundJobTrackerService implements OnDestroy {
  private readonly api = inject(CsrWebApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly runsSignal = signal<TrackedRunState[]>([]);
  readonly trackedRuns = this.runsSignal.asReadonly();
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.restoreFromSession();
    this.ensurePolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  track(context: TrackedRunContext, initialRun?: CareerRolePopulationRunResponse): void {
    const existing = this.runsSignal().find((r) => r.runId === context.runId);
    if (existing) {
      return;
    }
    const next: TrackedRunState = {
      ...context,
      run: initialRun ?? null,
      dismissed: false
    };
    this.runsSignal.update((list) => [next, ...list]);
    this.persistToSession();
    this.ensurePolling();
    void this.pollOnce();
    this.snackBar.open(`Queued: ${context.label}`, 'Close', { duration: 3000 });
  }

  dismiss(runId: string): void {
    this.runsSignal.update((list) =>
      list.map((r) => (r.runId === runId ? { ...r, dismissed: true } : r))
    );
    this.persistToSession();
  }

  activeCount(roleSlug?: string): number {
    return this.visibleRuns(roleSlug).filter((r) => r.run && !TERMINAL.has(r.run.status)).length;
  }

  visibleRuns(roleSlug?: string): TrackedRunState[] {
    return this.runsSignal().filter((r) => {
      if (r.dismissed) {
        return false;
      }
      if (roleSlug && r.roleSlug && r.roleSlug !== roleSlug) {
        return false;
      }
      return true;
    });
  }

  private ensurePolling(): void {
    const hasActive = this.runsSignal().some((r) => !r.dismissed && (!r.run || !TERMINAL.has(r.run.status)));
    if (hasActive && !this.pollTimer) {
      this.pollTimer = setInterval(() => void this.pollOnce(), POLL_MS);
    } else if (!hasActive) {
      this.stopPolling();
    }
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private async pollOnce(): Promise<void> {
    const pending = this.runsSignal().filter((r) => !r.dismissed && (!r.run || !TERMINAL.has(r.run.status)));
    if (!pending.length) {
      this.stopPolling();
      return;
    }
    await Promise.all(
      pending.map(async (tracked) => {
        try {
          const run = await firstValueFrom(this.api.getPopulationRun(tracked.runId));
          const wasTerminal = tracked.run && TERMINAL.has(tracked.run.status);
          this.runsSignal.update((list) =>
            list.map((r) => (r.runId === tracked.runId ? { ...r, run } : r))
          );
          if (!wasTerminal && TERMINAL.has(run.status)) {
            this.onTerminal(tracked, run);
          }
        } catch {
          // transient poll errors ignored
        }
      })
    );
    this.persistToSession();
    this.ensurePolling();
  }

  private onTerminal(tracked: TrackedRunState, run: CareerRolePopulationRunResponse): void {
    const lastMessage = run.history?.[run.history.length - 1]?.message;
    if (run.status === 'completed') {
      this.snackBar.open(lastMessage ?? `${tracked.label} completed`, 'Close', { duration: 5000 });
    } else {
      this.snackBar.open(run.failureReason ?? lastMessage ?? `${tracked.label} failed`, 'Close', {
        duration: 6000
      });
    }
    tracked.onComplete?.();
  }

  private persistToSession(): void {
    const payload: PersistedRun[] = this.runsSignal()
      .filter((r) => !r.dismissed && (!r.run || !TERMINAL.has(r.run.status)))
      .map((r) => ({
        runId: r.runId,
        label: r.label,
        roleSlug: r.roleSlug,
        operationType: r.operationType
      }));
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }

  private restoreFromSession(): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return;
      }
      const items = JSON.parse(raw) as PersistedRun[];
      for (const item of items) {
        this.track(item);
      }
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }
}